import {defineMessages, type MessageDescriptor} from 'react-intl';
import {Frown, Meh, Scale, Smile, type LucideIcon} from 'lucide-react';
import type {CallOutcome, CallReason, SentimentLabel} from '../../../../store/features/calls/callsService';

const reasonMessages = defineMessages({
    NEW_INQUIRY: {id: 'callInsights.reason.new-inquiry', defaultMessage: 'New inquiry'},
    PRICING_QUOTE: {id: 'callInsights.reason.pricing-quote', defaultMessage: 'Pricing or quote'},
    ORDER_BOOKING: {id: 'callInsights.reason.order-booking', defaultMessage: 'Order or booking'},
    APPOINTMENT: {id: 'callInsights.reason.appointment', defaultMessage: 'Appointment'},
    SUPPORT_ISSUE: {id: 'callInsights.reason.support-issue', defaultMessage: 'Support issue'},
    BILLING_PAYMENT: {id: 'callInsights.reason.billing-payment', defaultMessage: 'Billing or payment'},
    COMPLAINT: {id: 'callInsights.reason.complaint', defaultMessage: 'Complaint'},
    CANCELLATION: {id: 'callInsights.reason.cancellation', defaultMessage: 'Cancellation'},
    ACCOUNT_CHANGE: {id: 'callInsights.reason.account-change', defaultMessage: 'Account change'},
    DELIVERY_STATUS: {id: 'callInsights.reason.delivery-status', defaultMessage: 'Delivery status'},
    FOLLOW_UP: {id: 'callInsights.reason.follow-up', defaultMessage: 'Follow-up'},
    PARTNER_VENDOR: {id: 'callInsights.reason.partner-vendor', defaultMessage: 'Partner or vendor'},
    WRONG_NUMBER_SPAM: {id: 'callInsights.reason.wrong-number-spam', defaultMessage: 'Wrong number or spam'},
    OTHER: {id: 'callInsights.reason.other', defaultMessage: 'Other'},
});

const outcomeMessages = defineMessages({
    RESOLVED: {id: 'callInsights.outcome.resolved', defaultMessage: 'Resolved'},
    INFO_PROVIDED: {id: 'callInsights.outcome.info-provided', defaultMessage: 'Info provided'},
    FOLLOW_UP_NEEDED: {id: 'callInsights.outcome.follow-up-needed', defaultMessage: 'Follow-up needed'},
    CALLBACK_REQUESTED: {id: 'callInsights.outcome.callback-requested', defaultMessage: 'Callback requested'},
    MEETING_SCHEDULED: {id: 'callInsights.outcome.meeting-scheduled', defaultMessage: 'Meeting scheduled'},
    ORDER_PLACED: {id: 'callInsights.outcome.order-placed', defaultMessage: 'Order placed'},
    QUOTE_REQUESTED: {id: 'callInsights.outcome.quote-requested', defaultMessage: 'Quote requested'},
    ESCALATED: {id: 'callInsights.outcome.escalated', defaultMessage: 'Escalated'},
    UNRESOLVED: {id: 'callInsights.outcome.unresolved', defaultMessage: 'Unresolved'},
    VOICEMAIL: {id: 'callInsights.outcome.voicemail', defaultMessage: 'Voicemail'},
    NO_CONVERSATION: {id: 'callInsights.outcome.no-conversation', defaultMessage: 'No conversation'},
    OTHER: {id: 'callInsights.outcome.other', defaultMessage: 'Other'},
});

const sentimentMessages = defineMessages({
    POSITIVE: {id: 'callInsights.sentiment.positive', defaultMessage: 'Positive'},
    NEUTRAL: {id: 'callInsights.sentiment.neutral', defaultMessage: 'Neutral'},
    NEGATIVE: {id: 'callInsights.sentiment.negative', defaultMessage: 'Negative'},
    MIXED: {id: 'callInsights.sentiment.mixed', defaultMessage: 'Mixed'},
});

const sentimentHints = defineMessages({
    POSITIVE: {id: 'callInsights.sentiment-hint.positive', defaultMessage: 'How the other person felt, rated by AI: satisfied and friendly.'},
    NEUTRAL: {id: 'callInsights.sentiment-hint.neutral', defaultMessage: 'How the other person felt, rated by AI: calm, no strong emotions.'},
    NEGATIVE: {id: 'callInsights.sentiment-hint.negative', defaultMessage: 'How the other person felt, rated by AI: unhappy or frustrated.'},
    MIXED: {id: 'callInsights.sentiment-hint.mixed', defaultMessage: 'How the other person felt, rated by AI: satisfied at times, unhappy at others.'},
});

export const reasonLabel = (reason: CallReason): MessageDescriptor => reasonMessages[reason] ?? reasonMessages.OTHER;

export const outcomeLabel = (outcome: CallOutcome): MessageDescriptor => outcomeMessages[outcome] ?? outcomeMessages.OTHER;

export const sentimentLabel = (sentiment: SentimentLabel): MessageDescriptor => sentimentMessages[sentiment] ?? sentimentMessages.NEUTRAL;

export const sentimentHint = (sentiment: SentimentLabel): MessageDescriptor => sentimentHints[sentiment] ?? sentimentHints.NEUTRAL;

const sentimentIcons: Record<SentimentLabel, LucideIcon> = {
    POSITIVE: Smile,
    NEUTRAL: Meh,
    NEGATIVE: Frown,
    MIXED: Scale,
};

export const sentimentIcon = (sentiment: SentimentLabel): LucideIcon => sentimentIcons[sentiment] ?? Meh;

const sentimentTones: Record<SentimentLabel, string> = {
    POSITIVE: 'bg-success-tint text-success-ink',
    NEUTRAL: 'bg-surface-muted text-ink-secondary',
    NEGATIVE: 'bg-danger-tint text-danger-ink',
    MIXED: 'bg-warning-tint text-warning-ink',
};

export const sentimentTone = (sentiment: SentimentLabel): string => sentimentTones[sentiment] ?? sentimentTones.NEUTRAL;
